import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { addSongModels, updateSongModels } from '../../redux/actions/songModelActions';
import { fetchCategories } from '../../redux/actions/categoryActions';

const SongModelForm = ({ songModel, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { loading: savingSongModel, error: songModelError } = useSelector((state) => state.songModels);
  const { items: categories = [], loading: loadingCategories } = useSelector((state) => ({
    items: state.categories?.items || [],
    loading: state.categories?.loading || false
  }));

  const [formData, setFormData] = useState({
    model_name: '',
    model_thumbnail: '',
    model_id: '',
    model_url: ''
  });

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data if editing a songModel
  useEffect(() => {
    if (songModel) {
      setFormData({
        model_name: songModel.model_name || '',
        model_thumbnail: songModel.model_thumbnail || '',
        model_id: songModel.model_id || '',
        model_url: songModel.model_url || ''
      });
      if (songModel.model_thumbnail) {
        setImagePreview(songModel.model_thumbnail);
      }
      // Initialize selected categories if editing
      if (songModel.categories && Array.isArray(songModel.categories)) {
        setSelectedCategories(songModel.categories.map(cat => cat.id));
      } else {
        setSelectedCategories([]);
      }
    } else {
      setFormData({
        model_name: '',
        model_thumbnail: '',
        model_id: '',
        model_url: ''
      });
      setImagePreview('');
      setSelectedCategories([]);
    }
  }, [songModel]);

  // Fetch categories on mount
  useEffect(() => {
    dispatch(fetchCategories({page: 1, items_per_page: 100}));
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (e) => {
    const categoryId = parseInt(e.target.value);
    const isChecked = e.target.checked;
    
    if (isChecked) {
      setSelectedCategories(prev => [...prev, categoryId]);
    } else {
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.model_name.trim()) newErrors.model_name = 'Model name is required';
    if (!formData.model_url.trim()) newErrors.model_url = 'Model URL is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    const songModelFormData = new FormData();
    
    if (songModel) {
      // For update, include the model ID
      songModelFormData.append('id', songModel.id);
      songModelFormData.append('model_id', formData.model_id);
    }
    
    songModelFormData.append('model_name', formData.model_name);
    songModelFormData.append('model_url', formData.model_url);
    
    if (imageFile) {
      songModelFormData.append('model_thumbnail', imageFile);
    } else if (formData.model_thumbnail) {
      songModelFormData.append('model_thumbnail', formData.model_thumbnail);
    }
    
    // Add category IDs in the specified format
    selectedCategories.forEach((categoryId, index) => {
      songModelFormData.append(`category_ids[${index}]`, categoryId);
    });
    
    try {
      let response;
      if (songModel) {
        response = await dispatch(updateSongModels(songModelFormData));
      } else {
        response = await dispatch(addSongModels(songModelFormData));
      }
      if (response.success) {
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error saving songModel:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {songModelError && <Alert variant="danger">{songModelError}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col md={8}>
            <Form.Group className="mb-3">
              <Form.Label>Model Name</Form.Label>
              <Form.Control
                type="text"
                name="model_name"
                value={formData.model_name}
                onChange={handleChange}
                isInvalid={!!errors.model_name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.model_name}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Model URL</Form.Label>
              <Form.Control
                type="text"
                name="model_url"
                value={formData.model_url}
                onChange={handleChange}
                isInvalid={!!errors.model_url}
              />
              <Form.Control.Feedback type="invalid">
                {errors.model_url}
              </Form.Control.Feedback>
            </Form.Group>
            {songModel && (
              <Form.Group className="mb-3">
                <Form.Label>Model ID</Form.Label>
                <Form.Control
                  type="text"
                  name="model_id"
                  value={formData.model_id}
                  disabled
                />
              </Form.Group>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Categories</Form.Label>
              {loadingCategories ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading categories...</span>
                </div>
              ) : (
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '10px' }}>
                  {categories.length === 0 ? (
                    <div className="text-muted">No categories available</div>
                  ) : (
                    categories.map((category) => (
                      <Form.Check
                        key={category.id}
                        type="checkbox"
                        id={`category-${category.id}`}
                        label={category.category_name}
                        value={category.id}
                        checked={selectedCategories.includes(category.id)}
                        onChange={handleCategoryChange}
                        className="mb-2"
                      />
                    ))
                  )}
                </div>
              )}
              <Form.Text className="text-muted">
                Select one or more categories to assign to this model
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Model Thumbnail</Form.Label>
              <div className="mb-2">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Model thumbnail preview"
                    style={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }}
                  />
                ) : (
                  <div
                    className="d-flex justify-content-center align-items-center bg-light"
                    style={{ height: '150px', border: '1px dashed #ccc' }}
                  >
                    No image
                  </div>
                )}
              </div>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                isInvalid={!!errors.model_thumbnail}
              />
              <Form.Control.Feedback type="invalid">
                {errors.model_thumbnail}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Recommended size: 500x750 pixels
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
        <div className="d-flex justify-content-end">
          <Button
            variant="secondary"
            className="me-2"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting || savingSongModel}
          >
            {isSubmitting || savingSongModel ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                {songModel ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              songModel ? 'Update Song Model' : 'Create Song Model'
            )}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default SongModelForm; 